import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  Paper,
  IconButton,
  Divider,
} from "@mui/material";
import { ThemeProvider } from "styled-components";
import { lightTheme } from "../theme";
import DataTable from "../ui/DataTable";
import ViewPdfDialog from "./ViewPdfDialog";
import axios from "axios";
import { AttachFile, PictureAsPdf, Delete } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useTheme, useMediaQuery } from "@mui/material";

const CourseRegister = () => {
  const { userId } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [step, setStep] = useState(1);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewCourseId, setViewCourseId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState('');
  const [instructions, setInstructions] = useState("");
  const [orgName, setOrgName] = useState("");
const [orgLogo, setOrgLogo] = useState("");
const [accessCodeValid, setAccessCodeValid] = useState(null);
const [checkingCode, setCheckingCode] = useState(false);
const themeMui = useTheme();
const isMobile = useMediaQuery(themeMui.breakpoints.down("sm"));
const [formData, setFormData] = useState({
  firstName: "",
  lastName: "",
  studentId: "",
  email: "",
  phone: "",
  accessCode: "",
  files: [],
});

  const [errors, setErrors] = useState({});

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
  const fetchOrg = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const response = await axios.get(`${apiUrl}/api/organization/${userId}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: getBasicAuthHeader(),
        },
      });
      if (response.data) {
        setOrgName(response.data.orgName || "");
        setOrgLogo(`data:image/jpeg;base64,${response.data.orgLogo}` || "");
      }
    } catch (error) {
      console.error("Error fetching organization data:", error);
    }
  };
  fetchOrg();
}, [userId]);

  useEffect(() => {
    fetch(apiUrl + `/api/course-list-using-userId/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: getBasicAuthHeader(),
      },
    })
      .then((resp) => resp.json())
      .then((resp) => setTableData(resp));
  }, [apiUrl, userId]);

   useEffect(() => {
      const fetchData = async () => {
        const apiUrl = process.env.REACT_APP_API_URL;
        try {
          const res = await axios.get(apiUrl+`/api/registration/${userId}`, {
            headers: {
              'Content-Type': "application/json",
              "Accept": "*/*",
              "Access-Control-Allow-Origin": "*",
              'Authorization': getBasicAuthHeader(),
            }
          });
          if (res.data) {
            setTheme(res.data.theme || "");
            setInstructions(res.data.instructions || "");
          }
        } catch (err) {
          console.error("Error fetching registration settings:", err);
        }
      };
  
      fetchData();
    }, [userId]);

  const handleApplyClick = (course) => {
    setSelectedCourse(course);
    setStep(2);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCourse(null);
    setStep(1);
    setFormData({
    firstName: "",
  lastName: "",
  studentId: "",
  email: "",
  phone: "",
  accessCode: "",
  files: [],
    });
    setErrors({});
  };

  const validateAccessCode = async (code) => {
  if (!code) return;

  setCheckingCode(true);
  try {
    const response = await axios.get(
      `${apiUrl}/api/validate-access-code/${code}`,
      {
        headers: {
            "Content-Type": "application/json",
            Authorization: getBasicAuthHeader(),
          },
      }
    );

    setAccessCodeValid(response.data?.valid === true);
  } catch (err) {
    console.error("Access code validation error:", err);
    setAccessCodeValid(false);
  } finally {
    setCheckingCode(false);
  }
};

  const handleFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setFormData((prev) => ({ ...prev, files: [...prev.files, ...files] }));
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const uploadSingleFile = async (file) => {
    const formData1 = new FormData();
    const customName = `${file.name}_${formData.email}`;
    formData1.append("name", customName);
    formData1.append("file", file);
    formData1.append("courseId", selectedCourse.courseId);
    formData1.append("source", "Student");

    const response = await axios.post(
      apiUrl + `/api/uploadPdf/${selectedCourse.courseId}`,
      formData1,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: getBasicAuthHeader(),
        },
      }
    );
    return response.data;
  };

  const validateForm = () => {
  let newErrors = {};

  if (!formData.firstName.trim()) {
    newErrors.firstName = "First Name is required";
  }

  if (!formData.lastName.trim()) {
    newErrors.lastName = "Last Name is required";
  }

  if (!formData.email.trim()) {
    newErrors.email = "Email is required";
  } else {
    // ✅ Email regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const handleChange = (e) => {
  const { name, value } = e.target;

  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));

  // ✅ Live validation
  setErrors((prevErrors) => {
    let updatedErrors = { ...prevErrors };

    if (name === "firstName" && !value.trim()) {
      updatedErrors.firstName = "First Name is required";
    } else if (name === "firstName") {
      delete updatedErrors.firstName;
    }

    if (name === "lastName" && !value.trim()) {
      updatedErrors.lastName = "Last Name is required";
    } else if (name === "lastName") {
      delete updatedErrors.lastName;
    }

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!value.trim()) {
        updatedErrors.email = "Email is required";
      } else if (!emailRegex.test(value)) {
        updatedErrors.email = "Enter a valid email";
      } else {
        delete updatedErrors.email;
      }
    }

    return updatedErrors;
  });
};

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (accessCodeValid !== true) {
    alert("❌ Please enter a valid access code before submitting.");
    return;
  }

  if (!validateForm()) return;

  setLoading(true);

  try {
    let fileUrls = [];

    for (const file of formData.files) {
      const url = await uploadSingleFile(file);
      fileUrls.push(url);
    }

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      accessCode: formData.accessCode,
      courseId: selectedCourse?.courseId,
      fileUrls,
      userId: userId,
      studentId: formData.studentId,
    };

    const response = await fetch(
      apiUrl + "/api/register-course-for-student-with-pdf",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getBasicAuthHeader(),
        },
        body: JSON.stringify(payload),
      }
    );

    // ✅ Read response body
    const responseData = await response.text();

    // ===============================
    // ✅ Already registered
    // ===============================
    if (response.status === 409) {
      alert(`⚠️ ${responseData}`);
      return;
    }

    // ===============================
    // ✅ Other API errors
    // ===============================
    if (!response.ok) {
      throw new Error(responseData || "Registration failed");
    }

    // ===============================
    // ✅ Success
    // ===============================
    alert(
      `You have successfully registered for ${selectedCourse?.courseName}. You will receive an email once your access has been approved.`
    );

    handleClose();

  } catch (error) {

    console.error("Error submitting form:", error);

    alert(
      `❌ ${error.message || "Something went wrong, please try again."}`
    );

  } finally {

    setLoading(false);

  }
};

  const handleDownloadForm = (courseId) => {
    setViewCourseId(courseId);
    setViewDialogOpen(true);
  };

  const columnDefs = [
    { headerName: "Course Name", field: "courseName", flex: 1.5 },
     ...(!isMobile
    ? [{ headerName: "Description", field: "description", flex: 2 }]
    : []),
    {
      headerName: "Actions",
      field: "action",
      flex: 1.5,
      cellRenderer: (params) => (
        <motion.div
          whileHover={{ scale: 1.05 }}
          style={{ display: "flex", gap: "8px" }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "20px",
              fontWeight: "bold",
              borderColor: "secondary.main",
              color: "secondary.main",
            }}
            size="small"
            onClick={() => handleApplyClick(params.data)}
            disabled={loading}
          >
            Register
          </Button>
        </motion.div>
      ),
    },
  ];

  const handleViewPdfClick = (courseId) => {
    setViewCourseId(courseId);
    setViewDialogOpen(true);
  };

  const adjustColor = (hex, amount) => {
  let col = hex.startsWith("#") ? hex.slice(1) : hex;
  let num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;

  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  return `rgb(${r}, ${g}, ${b})`;
};

  return (
    <ThemeProvider theme={lightTheme}>
    <Box
      sx={{
        height: "100vh",
        p: 3,
        bgcolor: "linear-gradient(135deg, #f6f9fc, #eef2f7)",
      }}
    >
      {/* Header */}
    <motion.div
  initial={{ opacity: 0, y: -40 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7, ease: "easeOut" }}
>
  <Paper
    elevation={6}
    sx={{
      p: 3,
      mb: 4,
      borderRadius: 4,
      background: `linear-gradient(135deg, ${theme || "#5b54e6"}, ${adjustColor(theme || "#5b54e6", 50)})`,
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0px 8px 25px rgba(0,0,0,0.15)",
    }}
  >
    {/* Left: Org Logo + Name */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <motion.img
        src={orgLogo}
        alt="Organization Logo"
        style={{
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          objectFit: "cover",
          border: "3px solid white",
        }}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
      />
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{ textShadow: "1px 1px 3px rgba(0,0,0,0.2)" }}
      >
        {orgName || "Organization Name"}
      </Typography>
    </Box>

    {/* Center: Available Courses */}
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{
          textAlign: "center",
          flexGrow: 1,
          textShadow: "1px 1px 4px rgba(0,0,0,0.3)",
          letterSpacing: 1,
        }}
      >
        📚 Available Courses
      </Typography>
    </motion.div>

    {/* Right Side (Optional future use) */}
    <Box sx={{ width: "60px" }} /> {/* Keeps spacing symmetrical */}
  </Paper>
</motion.div>
      {/* Course Grid */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <DataTable
          rows={tableData || []}
          rowKey={(r, i) => r.courseId ?? r.id ?? i}
          search
          searchPlaceholder="Search courses…"
          searchKeys={["courseName", "description"]}
          primary={{
            header: "Course",
            label: (r) => r.courseName,
          }}
          columns={[
            {
              header: "Description",
              muted: true,
              render: (r) => (
                <div
                  title={r.description || ""}
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: isMobile ? 150 : 360,
                    cursor: r.description ? "help" : "default",
                    lineHeight: 1.4,
                  }}
                >
                  {r.description}
                </div>
              ),
            },
          ]}
          actions={(r) => (
            <Button
              variant="outlined"
              sx={{ borderRadius: "20px", fontWeight: "bold", borderColor: "secondary.main", color: "secondary.main" }}
              size="small"
              onClick={() => handleApplyClick(r)}
              disabled={loading}
            >
              Register
            </Button>
          )}
          emptyText="No courses available."
        />
      </motion.div>

      {/* Register Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
  sx={{
    bgcolor: theme || "primary.main",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "1.25rem",
  }}
>
  Register For {selectedCourse?.courseName} Course
</DialogTitle>

        {/* Step 2 */}
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
  <TextField
    fullWidth
    label="Student ID"
    name="studentId"
    value={formData.studentId}
    onChange={handleChange}
    error={!!errors.studentId}
    helperText={errors.studentId}
  />
</Grid>
                <Grid item xs={12}>
  <TextField
    fullWidth
    type="email"
    label="Email"
    name="email"
    value={formData.email}
    onChange={handleChange}
    required
    error={!!errors.email}
    helperText={errors.email}
  />
</Grid>

<Grid item xs={12}>
  <TextField
    fullWidth
    label="Phone Number (Optional)"
    name="phone"
    value={formData.phone}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12}>
  <TextField
    fullWidth
    label="Access Code"
    name="accessCode"
    value={formData.accessCode}
    onChange={(e) =>
      setFormData((prev) => ({
        ...prev,
        accessCode: e.target.value.toUpperCase(),
      }))
    }
    onBlur={() => validateAccessCode(formData.accessCode)}
    error={accessCodeValid === false}
    helperText={
      checkingCode
        ? "Checking..."
        : accessCodeValid === false
        ? "Invalid Access Code"
        : accessCodeValid === true
        ? "Valid Access Code"
        : ""
    }
  />
</Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<AttachFile />}
                    disabled={loading}
                  >
                    Attach PDF(s)
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={handleFileChange}
                      accept=".pdf"
                    />
                  </Button>
                  {errors.files && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {errors.files}
                    </Typography>
                  )}

                  {formData.files.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {formData.files.map((file, idx) => (
                        <Paper
                          key={idx}
                          variant="outlined"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1,
                            mb: 1,
                            borderRadius: 3,
                            boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                          }}
                        >
                          <PictureAsPdf color="error" sx={{ mr: 1 }} />
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFile(idx)}
                          >
                            <Delete color="error" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
  type="submit"
  variant="contained"
  color="primary"
  disabled={
    loading ||
    checkingCode ||
    accessCodeValid !== true ||   // access code must be valid
    !formData.firstName.trim() || // required fields
    !formData.lastName.trim() ||
    !formData.email.trim() ||
    Object.keys(errors).length > 0 // any validation errors
  }
>
  {loading ? "Submitting..." : "Submit"}
</Button>
            </DialogActions>
          </form>
      </Dialog>

      <ViewPdfDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        courseId={viewCourseId}
        source="Instructor"
      />
    </Box>
    </ThemeProvider>
  );
};

export default CourseRegister;